/** Accounts, roles and permissions (api/src/users, api/src/auth/permissions.ts). */
import type { BaseEntity } from './common';

export const STAFF_ROLES = ['admin', 'product_manager', 'sales_lead', 'support'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];
/** `prospect` = an account with no tenant yet (public sign-up). */
export type TenantRole = 'prospect' | 'owner' | 'member';
export type Role = StaffRole | TenantRole;
/** What an account on a tenant can be (the roles an admin may assign to a tenant account). */
export const MEMBER_ROLES = ['owner', 'member'] as const;

/**
 * Coarse access tags the API derives from user_type / role on every request
 * (api/src/auth/access.ts): admin = every route (an admin is staff too), staff = any
 * staff role, tenant = a tenant account pinned to its own tenant.
 */
export type AccessTag = 'admin' | 'staff' | 'tenant';

/** Permission names the API grants per role (api/src/auth/permissions.ts). */
export type Permission =
  | 'users:read'
  | 'users:write'
  | 'users:roles'
  | 'users:support'
  | 'tenants:read'
  | 'tenants:write'
  | 'tenants:assign'
  | 'whitelist:read'
  | 'whitelist:manage'
  | 'jobs:read'
  | 'notifications:read'
  | 'notifications:manage'
  | 'billing:read'
  | 'billing:manage'
  | 'provisioning:read'
  | 'provisioning:manage'
  | 'tenant:application:read'
  | 'tenant:billing:read'
  | 'tenant:billing:update'
  | 'tenant:dashboard:read'
  | 'tenant:performance:read'
  | 'tenant:team:read'
  | 'tenant:team:manage'
  | 'tenant:domains:read'
  | 'tenant:domains:manage'
  | 'whatsapp:read'
  | 'whatsapp:manage'
  | 'tenant:whatsapp:read'
  | 'tenant:whatsapp:manage'
  | 'tenant:whatsapp:connect'
  // operations (admin only)
  | 'status:read'
  | 'cloudflare:read'
  // the security audit trail (admin, support)
  | 'audit:read';

export type UserStatus = 'PENDING' | 'ACTIVE' | 'INACTIVE';
export type UserType = 'staff' | 'tenant';

/**
 * Derived by the API from the row (api/src/auth/state/account-state.ts):
 * INACTIVE and DISABLED cannot sign in, DEACTIVATED sees basic details only,
 * LOCKED cannot start a new sign-in until `lockedUntil`, UNVERIFIED has not
 * finished confirming, ACTIVE is the normal case.
 */
export type AccountState =
  'UNVERIFIED' | 'ACTIVE' | 'LOCKED' | 'DISABLED' | 'DEACTIVATED' | 'INACTIVE';

export const ACCOUNT_STATE_LABELS: Record<AccountState, string> = {
  UNVERIFIED: 'Unverified',
  ACTIVE: 'Active',
  LOCKED: 'Locked',
  DISABLED: 'Disabled',
  DEACTIVATED: 'Deactivated',
  INACTIVE: 'Inactive',
};

export interface User extends BaseEntity {
  status: UserStatus;
  username: string;
  email: string;
  /** The provider's given_name / family_name; null when the account carries neither. */
  firstName: string | null;
  lastName: string | null;
  userType: UserType;
  role: Role;
  providerUserId: string | null;
  /** E.164, mirrors the number held at the provider. */
  phone: string | null;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  isMfaEnabled: boolean;
  isProviderEnabled: boolean;
  /** Switched off by staff: can sign in, sees basic details only, nothing else works. */
  isDeactivated: boolean;
  deactivatedAt: string | null;
  /** Set while a lockout is in force (too many wrong passwords). */
  lockedUntil: string | null;
  /** When the account opted out of promotional email; null = eligible. */
  marketingOptOutAt: string | null;
  accountState: AccountState;
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  product_manager: 'Product manager',
  sales_lead: 'Sales lead',
  support: 'Customer support',
  owner: 'Owner',
  member: 'Member',
  prospect: 'Prospect',
};

/**
 * What to call a person in the UI: their name, else the username (accounts
 * created before names existed), else the email. New usernames are generated
 * UUIDs and are never shown on their own.
 */
export function displayName(u: {
  firstName: string | null;
  lastName: string | null;
  username?: string;
  email: string;
}): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return name || u.username || u.email;
}

/** The API's RequestPrincipal: who the bearer token resolved to. */
export interface Principal {
  userId: string;
  principalId: string;
  actorKind: UserType;
  tenantId: string | null;
  role: Role;
  /** Empty while deactivated. */
  access: AccessTag[];
  /** Empty while deactivated. */
  permissions: Permission[];
  deactivated: boolean;
}

/** GET /auth/me */
export interface MeResponse {
  user: User;
  principal: Principal;
}
